import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

export type ThemePreference = 'system' | 'light' | 'dark'
const KEY = 'nolens-vacations.theme'

const ThemeCtx = createContext<{ preference: ThemePreference; resolved: 'light' | 'dark'; setPreference: (p: ThemePreference) => void } | null>(null)

function read(): ThemePreference {
  try {
    const v = localStorage.getItem(KEY)
    return v === 'light' || v === 'dark' ? v : 'system'
  } catch {
    return 'system'
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPref] = useState<ThemePreference>(read)
  const [systemDark, setSystemDark] = useState(() => window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false)

  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)')
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches)
    mq?.addEventListener('change', handler)
    return () => mq?.removeEventListener('change', handler)
  }, [])

  const resolved = preference === 'system' ? (systemDark ? 'dark' : 'light') : preference

  useEffect(() => {
    document.documentElement.dataset.theme = resolved
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', resolved === 'dark' ? '#0d0e10' : '#f7f7f5')
  }, [resolved])

  const setPreference = (p: ThemePreference) => {
    try {
      localStorage.setItem(KEY, p)
    } catch {
      // ignore
    }
    setPref(p)
  }

  return <ThemeCtx.Provider value={{ preference, resolved, setPreference }}>{children}</ThemeCtx.Provider>
}

export function useTheme() {
  const v = useContext(ThemeCtx)
  if (!v) throw new Error('useTheme fora do ThemeProvider')
  return v
}
